import { useState, useMemo } from 'react';
import { Plus, X } from 'lucide-react';
import type { UsersAndTeams } from '@momentum/types';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/Popover';
import { usePeopleTeamData } from '@/shared/components/people-team-list/PeopleTeamList';
import {
  PersonAvatar,
  TeamIcon,
} from '@/shared/components/people-team-list/PeopleTeamAvatars';
import { UserTeamPickerPopover } from './UserTeamPickerPopover';

interface UserTeamSelectProps {
  value: UsersAndTeams;
  onChange: (value: UsersAndTeams) => void;
  placeholder?: string;
  teamsLabel?: string;
  peopleLabel?: string;
  searchPlaceholder?: string;
  noResultsLabel?: string;
}

export const UserTeamSelect = ({
  value,
  onChange,
  placeholder = 'Add',
  teamsLabel = 'Teams',
  peopleLabel = 'People',
  searchPlaceholder = 'Search...',
  noResultsLabel = 'No results found',
}: UserTeamSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { teamItems, personItems } = usePeopleTeamData();

  const selectedTags = useMemo(() => {
    const teamTags = value.teams
      .map((teamId) => {
        const team = teamItems.find((teamItem) => teamItem.id === teamId);
        return team
          ? { type: 'team' as const, id: teamId, label: team.name }
          : null;
      })
      .filter(Boolean);

    const userTags = value.users
      .map((userId) => {
        const person = personItems.find(
          (personItem) => personItem.id === userId
        );
        return person
          ? {
              type: 'user' as const,
              id: userId,
              label: person.name,
              initials: person.initials,
              imageUrl: person.imageUrl,
            }
          : null;
      })
      .filter(Boolean);

    return [...teamTags, ...userTags];
  }, [value, teamItems, personItems]);

  const removeItem = (type: 'user' | 'team', id: string) => {
    if (type === 'team') {
      onChange({
        ...value,
        teams: value.teams.filter((teamId) => teamId !== id),
      });
    } else {
      onChange({
        ...value,
        users: value.users.filter((userId) => userId !== id),
      });
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1 py-1">
        {selectedTags.map((tag) =>
          tag ? (
            <span
              key={`${tag.type}-${tag.id}`}
              className="inline-flex items-center gap-1.5 py-0.5 ps-1.5 pe-1 bg-[#f0f1f3] rounded text-[12.5px] font-medium text-foreground animate-in fade-in zoom-in-95 duration-150"
            >
              {tag.type === 'team' ? (
                <TeamIcon size="sm" />
              ) : (
                <PersonAvatar
                  initials={'initials' in tag ? (tag.initials as string) : ''}
                  imageUrl={
                    'imageUrl' in tag ? (tag.imageUrl as string) : undefined
                  }
                  size="sm"
                />
              )}
              {tag.label}
              <button
                type="button"
                className="flex items-center justify-center w-4 h-4 rounded-sm text-muted-foreground hover:bg-black/[0.06] hover:text-foreground transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  removeItem(tag.type, tag.id);
                }}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ) : null
        )}

        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[12px] text-muted-foreground border border-dashed border-border bg-transparent hover:text-foreground/70 hover:border-muted-foreground hover:bg-muted transition-colors cursor-pointer"
            >
              <Plus className="w-3 h-3" />
              {placeholder}
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="min-w-[240px] w-auto p-0 shadow-[0_8px_32px_rgba(0,0,0,0.10)]"
            align="start"
            sideOffset={4}
          >
            <UserTeamPickerPopover
              value={value}
              onChange={onChange}
              searchPlaceholder={searchPlaceholder}
              teamsLabel={teamsLabel}
              peopleLabel={peopleLabel}
              noResultsLabel={noResultsLabel}
              onEscape={() => setIsOpen(false)}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
